import { DeleteOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons';
import {
  IssueCaseCategory,
  IssueLiabilityCostType,
  IssueLiabilityDoc,
  IssueLiabilityResponsibleType,
  IssueLiabilityStatus,
  IssueProduct,
  IssueSourceType,
  issueLiabilityCostTypeMap,
  issueLiabilityResponsibleTypeMap,
  issueLiabilityStatusMap,
} from '@gooduncles/gu-app-schema';
import { Button, Form, Modal, Popconfirm, Table, Tag, Tooltip, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from 'src/stores/auth-context';
import {
  deleteIssueLiability,
  getOrder,
  getProductInventory,
  setIssueLiability,
} from 'src/utils/firebase/firebase-short-cut';
import { createIssueLiability, updateIssueLiabilityWithStatusHistory } from 'src/utils/issue/issue-util';

import { AgCellIssueLiabilityResponsibleDisplay } from 'src/atomic-components/atoms/AgCell/Issue/AgCellIssueLiabilityResponsibleDisplay';

import {
  LiabilityForm,
  LiabilityFormValues,
  LiabilityHistoryManager,
  formatAmount,
  liabilityCostTypeColorMap,
  liabilityResponsibleTypeColorMap,
  liabilityStatusColorMap,
} from './components';
import { IssueManageMasterRowData } from './IssueManage.types';

interface IssueLiabilityModalProps {
  open: boolean;
  onClose: () => void;
  issueCase: IssueManageMasterRowData;
  /** 모달 열릴 때 바로 편집 모드로 시작할 liability ID */
  initialEditLiabilityId?: string | null;
}

const IssueLiabilityModal: FC<IssueLiabilityModalProps> = ({ open, onClose, issueCase, initialEditLiabilityId }) => {
  const { user } = useAuth();
  const [form] = Form.useForm<LiabilityFormValues>();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deliveryPartnerId, setDeliveryPartnerId] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  // responsibleType 변경 감지
  const responsibleType = Form.useWatch('responsibleType', form);

  const currentUserId = user?._id ?? '';
  const liabilities = useMemo(() => issueCase.issueLiabilities ?? [], [issueCase.issueLiabilities]);

  /**
   * 모달 열릴 때 initialEditLiabilityId가 있으면 해당 항목 편집 모드로 시작
   */
  useEffect(() => {
    if (open && initialEditLiabilityId) {
      const targetLiability = (issueCase.issueLiabilities ?? []).find((l) => l._id === initialEditLiabilityId);
      if (targetLiability) {
        form.setFieldsValue({
          responsibleType: targetLiability.responsibleType,
          responsibleId: targetLiability.responsibleId,
          supplyAmount: targetLiability.amount.supplyAmount,
          taxAmount: targetLiability.amount.taxAmount,
          memo: targetLiability.memo,
          progressNote: targetLiability.progressNote,
          status: targetLiability.status,
          costType: targetLiability.costType,
        });
        setEditingId(targetLiability._id);
        setIsAdding(false);
      }
    }
  }, [open, initialEditLiabilityId, issueCase.issueLiabilities, form]);

  /**
   * DELIVERY 카테고리이고 sourceType이 order인 경우 주문 정보에서 deliveryPartnerId 조회
   */
  useEffect(() => {
    const fetchDeliveryPartnerId = async () => {
      if (
        issueCase.category !== IssueCaseCategory.DELIVERY ||
        issueCase.sourceType !== IssueSourceType.ORDER ||
        !issueCase.sourceId
      ) {
        setDeliveryPartnerId(null);
        return;
      }

      try {
        const order = await getOrder(issueCase.sourceId);
        setDeliveryPartnerId(order?.deliveryPartnerId ?? null);
      } catch (error) {
        console.error('Failed to fetch order:', error);
        setDeliveryPartnerId(null);
      }
    };

    fetchDeliveryPartnerId();
  }, [issueCase.category, issueCase.sourceType, issueCase.sourceId]);

  /**
   * 새 항목 추가 폼 표시
   */
  const handleShowAddForm = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({
      responsibleType: 'operator',
      responsibleId: null,
      supplyAmount: 0,
      taxAmount: 0,
      memo: null,
      progressNote: null,
      status: IssueLiabilityStatus.registered,
      costType: IssueLiabilityCostType.general,
    });
    setIsAdding(true);
    setEditingId(null);
  }, [form]);

  /**
   * 상품 선택 시 책임자 유형과 책임자, 금액 자동 설정
   * - SUPPLIER, QUALITY: 거래처 → inventory.purchaseConfig.supplierId
   * - DELIVERY: 파트너스 → order.deliveryPartnerId (sourceType이 order인 경우만)
   * - ETC: 자동 채우기 없음 (메모, 금액만 입력)
   */
  const handleProductSelect = useCallback(
    async (product: IssueProduct) => {
      // 메모에 상품명 입력
      form.setFieldValue('memo', product.productName);

      // 공급가, 세금 자동 입력
      if (product.amount) {
        form.setFieldValue('supplyAmount', product.amount.supplyAmount);
        form.setFieldValue('taxAmount', product.amount.taxAmount);
      }

      const category = issueCase.category;

      // SUPPLIER 또는 QUALITY 카테고리: 거래처로 설정
      if (category === IssueCaseCategory.SUPPLIER || category === IssueCaseCategory.QUALITY) {
        form.setFieldValue('responsibleType', 'productSupplier');

        // inventory에서 supplierId 조회
        try {
          const inventory = await getProductInventory(product.inventoryId);
          if (inventory?.purchaseConfig?.supplierId) {
            form.setFieldValue('responsibleId', inventory.purchaseConfig.supplierId);
          }
        } catch (error) {
          console.error('Failed to fetch inventory:', error);
        }
        return;
      }

      // DELIVERY 카테고리: 파트너스로 설정
      if (category === IssueCaseCategory.DELIVERY) {
        form.setFieldValue('responsibleType', 'partners');

        // sourceType이 order이고 deliveryPartnerId가 있는 경우 자동 설정
        if (deliveryPartnerId) {
          form.setFieldValue('responsibleId', deliveryPartnerId);
        }
        return;
      }

      // ETC 카테고리: 자동 채우기 없음 (메모, 금액만 입력)
    },
    [form, issueCase.category, deliveryPartnerId],
  );

  /**
   * 편집 시작
   */
  const handleEdit = useCallback(
    (record: IssueLiabilityDoc) => {
      form.setFieldsValue({
        responsibleType: record.responsibleType,
        responsibleId: record.responsibleId,
        supplyAmount: record.amount.supplyAmount,
        taxAmount: record.amount.taxAmount,
        memo: record.memo,
        progressNote: record.progressNote,
        status: record.status,
        costType: record.costType,
      });
      setEditingId(record._id);
      setIsAdding(false);
    },
    [form],
  );

  /**
   * 폼 취소
   */
  const handleCancel = useCallback(() => {
    form.resetFields();
    setIsAdding(false);
    setEditingId(null);
  }, [form]);

  /**
   * 저장 (추가 또는 편집)
   * - 편집 시 상태가 변경되면 히스토리에 자동으로 기록
   */
  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const totalAmount = (values.supplyAmount ?? 0) + (values.taxAmount ?? 0);

      if (editingId) {
        // 편집 모드: updateIssueLiabilityWithStatusHistory 사용 (상태 변경 시 히스토리 자동 추가)
        const currentLiability = liabilities.find((l) => l._id === editingId);
        if (!currentLiability) {
          message.error('손실 책임 정보를 찾을 수 없습니다.');
          return;
        }

        await updateIssueLiabilityWithStatusHistory(
          editingId,
          {
            responsibleType: values.responsibleType,
            responsibleId: values.responsibleId || null,
            amount: {
              supplyAmount: values.supplyAmount ?? 0,
              taxAmount: values.taxAmount ?? 0,
              totalAmount,
            },
            memo: values.memo || null,
            progressNote: values.progressNote || null,
            status: values.status,
            costType: values.costType,
          },
          currentLiability,
          currentUserId,
        );
        message.success('수정되었습니다.');
      } else {
        // 추가 모드: createIssueLiability로 초기 데이터 생성
        const newLiability = createIssueLiability({
          issueCaseId: issueCase._id,
          responsibleType: values.responsibleType,
          responsibleId: values.responsibleId || null,
          amount: {
            supplyAmount: values.supplyAmount ?? 0,
            taxAmount: values.taxAmount ?? 0,
          },
          memo: values.memo || null,
          progressNote: values.progressNote || null,
          status: values.status,
          costType: values.costType,
          createdBy: currentUserId,
        });
        await setIssueLiability(undefined, newLiability);
        message.success('추가되었습니다.');
      }

      handleCancel();
    } catch (error) {
      console.error('Save failed:', error);
      message.error('저장에 실패했습니다.');
    }
  }, [form, issueCase._id, editingId, handleCancel, currentUserId, liabilities]);

  /**
   * 삭제
   */
  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteIssueLiability(id);
      message.success('삭제되었습니다.');
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('삭제에 실패했습니다.');
    }
  }, []);

  /**
   * 테이블 컬럼 정의
   */
  const columns: ColumnsType<IssueLiabilityDoc> = [
    {
      title: '책임자 유형',
      dataIndex: 'responsibleType',
      key: 'responsibleType',
      width: 120,
      render: (type: IssueLiabilityResponsibleType) => (
        <Tag color={liabilityResponsibleTypeColorMap[type]}>{issueLiabilityResponsibleTypeMap[type]}</Tag>
      ),
    },
    {
      title: '책임자',
      dataIndex: 'responsibleId',
      key: 'responsibleId',
      width: 150,
      render: (_: string | null, record: IssueLiabilityDoc) => (
        <AgCellIssueLiabilityResponsibleDisplay
          responsibleType={record.responsibleType}
          responsibleId={record.responsibleId ?? undefined}
        />
      ),
    },
    {
      title: '공급가',
      dataIndex: ['amount', 'supplyAmount'],
      key: 'supplyAmount',
      width: 120,
      align: 'right',
      render: (amount: number) => formatAmount(amount),
    },
    {
      title: '세금',
      dataIndex: ['amount', 'taxAmount'],
      key: 'taxAmount',
      width: 100,
      align: 'right',
      render: (amount: number) => formatAmount(amount),
    },
    {
      title: '총액',
      dataIndex: ['amount', 'totalAmount'],
      key: 'totalAmount',
      width: 120,
      align: 'right',
      render: (amount: number) => <strong>{formatAmount(amount)}</strong>,
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: IssueLiabilityStatus) => (
        <Tag color={liabilityStatusColorMap[status]}>{issueLiabilityStatusMap[status]}</Tag>
      ),
    },
    {
      title: '비용 유형',
      dataIndex: 'costType',
      key: 'costType',
      width: 90,
      render: (costType: IssueLiabilityCostType) => (
        <Tag color={liabilityCostTypeColorMap[costType]}>{issueLiabilityCostTypeMap[costType]}</Tag>
      ),
    },
    {
      title: '메모',
      dataIndex: 'memo',
      key: 'memo',
      width: 120,
      ellipsis: true,
      render: (memo: string | null) => memo || '-',
    },
    {
      title: '진행 노트',
      dataIndex: 'progressNote',
      key: 'progressNote',
      width: 150,
      ellipsis: true,
      render: (progressNote: string | null) => progressNote || '-',
    },
    {
      title: '작업',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type='link' size='small' onClick={() => handleEdit(record)}>
            편집
          </Button>
          <Popconfirm
            title='삭제하시겠습니까?'
            onConfirm={() => handleDelete(record._id)}
            okText='삭제'
            cancelText='취소'
          >
            <Button type='link' size='small' danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  /**
   * 총 손실액 계산
   */
  const totalLoss = liabilities.reduce((sum, l) => sum + l.amount.totalAmount, 0);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 24 }}>
          <span>손실 책임 관리 - {issueCase.storeName}</span>
          <span style={{ fontSize: 14, color: '#8c8c8c' }}>총 손실액: {formatAmount(totalLoss)}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      afterClose={() => {
        form.resetFields();
        setIsAdding(false);
        setEditingId(null);
        setExpandedRowKeys([]);
      }}
      width={1300}
      footer={null}
    >
      {/* 테이블 */}
      <Table<IssueLiabilityDoc>
        dataSource={liabilities}
        columns={columns}
        rowKey='_id'
        pagination={false}
        size='small'
        bordered
        style={{ marginBottom: 16 }}
        locale={{ emptyText: '등록된 손실 책임이 없습니다.' }}
        expandable={{
          expandedRowRender: (record) => <LiabilityHistoryManager liability={record} currentUserId={currentUserId} />,
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
          expandIcon: ({ expanded, onExpand, record }) => (
            <Tooltip title={expanded ? '히스토리 닫기' : '히스토리 보기'}>
              <Button
                type='text'
                size='small'
                icon={<HistoryOutlined style={{ color: expanded ? '#1890ff' : undefined }} />}
                onClick={(e) => onExpand(record, e)}
              />
            </Tooltip>
          ),
        }}
      />

      {/* 추가/편집 폼 */}
      {(isAdding || editingId) && (
        <LiabilityForm
          form={form}
          responsibleType={responsibleType}
          onSave={handleSave}
          onCancel={handleCancel}
          isEditing={!!editingId}
          editingLiability={editingId ? liabilities.find((l) => l._id === editingId) : null}
          products={issueCase.products}
          onProductSelect={handleProductSelect}
        />
      )}

      {/* 추가 버튼 */}
      {!isAdding && !editingId && (
        <Button type='dashed' icon={<PlusOutlined />} onClick={handleShowAddForm} block>
          손실 책임 추가
        </Button>
      )}
    </Modal>
  );
};

export default IssueLiabilityModal;
